// frontend/src/services/api.js

import axios from 'axios'
import { useAuthStore } from '../store/authStore'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://vizzy.technology'

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Флаг для предотвращения множественных refresh запросов
let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })

  isRefreshing = false
  failedQueue = []
}

// Interceptor для добавления токена в запрос
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Interceptor для обработки ошибок и refresh token
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // Если ошибка 401 и это не retry
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          return api(originalRequest)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const refreshToken = useAuthStore.getState().refreshToken

        if (!refreshToken) {
          throw new Error('No refresh token available')
        }

        const response = await axios.post(
          `${API_BASE_URL}/api/auth/refresh`,
          { refresh_token: refreshToken }
        )

        const { access_token, refresh_token } = response.data
        const { user } = useAuthStore.getState()

        useAuthStore.getState().setAuth(access_token, refresh_token, user)
        api.defaults.headers.common.Authorization = `Bearer ${access_token}`
        originalRequest.headers.Authorization = `Bearer ${access_token}`

        processQueue(null, access_token)
        return api(originalRequest)
      } catch (err) {
        processQueue(err, null)
        useAuthStore.getState().logout()
        window.location.href = '/login'
        return Promise.reject(err)
      }
    }

    return Promise.reject(error)
  }
)

// Auth API
export const authAPI = {
  // Локальная регистрация
  register: (data) => api.post('/api/auth/register', data),

  // Локальный вход
  login: (data) => api.post('/api/auth/login', data),

  // Обновить токен
  refreshToken: (refreshToken) =>
    api.post('/api/auth/refresh', { refresh_token: refreshToken }),

  // Выход
  logout: (refreshToken) =>
    api.post('/api/auth/logout', null, {
      params: { refresh_token: refreshToken },
    }),

  // Получить текущего пользователя
  getCurrentUser: () => api.get('/api/auth/me'),

  // Проверить токен
  verifyToken: (token) =>
    api.post('/api/auth/verify-token', null, {
      params: { token },
    }),

  // OAuth Google - начало
  startGoogleOAuth: () => api.get('/api/auth/oauth/google/start'),

  // OAuth Google - callback
  googleOAuthCallback: (data) =>
    api.post('/api/auth/oauth/google/callback', data),

  // OAuth GitHub - начало
  startGitHubOAuth: () => api.get('/api/auth/oauth/github/start'),

  // OAuth GitHub - callback
  gitHubOAuthCallback: (data) =>
    api.post('/api/auth/oauth/github/callback', data),

  // OAuth Yandex - начало
  startYandexOAuth: () => api.get('/api/auth/oauth/yandex/start'),

  // OAuth Yandex - callback
  yandexOAuthCallback: (data) =>
    api.post('/api/auth/oauth/yandex/callback', data),
}

// Projects API
export const projectsAPI = {
  list: () => api.get('/api/projects'),
  getDetails: (cardId) => api.get(`/api/projects/${cardId}`),
  delete: (cardId) => api.delete(`/api/projects/${cardId}`),
  create: (data) => api.post('/api/projects/', { name: data }),
  analyze: (cardId) =>
    api.get(`/api/projects/${cardId}/optimization-analysis`),
  downloadReport: (cardId) =>
    api.get(`/api/projects/${cardId}/report`, {
      responseType: 'blob',
    }),
}

// Technical Cards API
export const technicalCardAPI = {
  upload: (formData) =>
    api.post('/api/technical-cards/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  getSheets: (cardId) => api.get(`/api/technical-cards/${cardId}/sheets`),
  classifySheet: (sheetId, data) =>
    api.put(`/api/technical-cards/${sheetId}/classify`, data),
  getDetails: (cardId) => api.get(`/api/technical-cards/${cardId}`),
  getSheet: (sheetId) => api.get(`/api/technical-cards/sheets/${sheetId}`),
  analyzeSheet: (sheetId) =>
    api.post(`/api/technical-cards/${sheetId}/analyze`),
  // Полнотекстовый поиск по листам документа
  searchInDocument: (cardId, query, limit = 20) =>
    api.get(`/api/technical-cards/${cardId}/search`, {
      params: { q: query, limit }
    }),
  // Глобальный поиск по нескольким проектам (для сайдбара)
  searchMultiDocuments: (query, cardIds = [], limitPerCard = 5) =>
    api.post('/api/technical-cards/search-multi', {
      q: query,
      card_ids: cardIds,
      limit_per_card: limitPerCard
    }),
}

// Estimates API
export const estimateAPI = {
  getEstimates: (cardId) => api.get(`/api/verification/${cardId}/estimates`),
  verify: (data) => api.post('/api/verification/verify', data),
}

// Preparation API
export const preparationAPI = {
  uploadMaterials: (file) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post('/api/preparation/upload-materials', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  uploadEquipment: (file) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post('/api/preparation/upload-equipment', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  uploadOperations: (file) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post('/api/preparation/upload-operations', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
}

// References API
export const referencesAPI = {
  upload: (type, formData) =>
    api.post(`/api/references/upload/${type}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  uploadExcel: (type, formData) =>
    api.post(`/api/references/upload-excel/${type}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  getTemplate: (type) => api.get(`/api/references/template/${type}`),
  list: (type, query = '') =>
    api.get(`/api/references/${type}`, { params: { q: query } }),
  delete: (type, itemId) =>
    api.delete(`/api/references/${type}/${itemId}`),
  getInfo: () => api.get('/api/references/info'),
  getRegions: () => api.get('/api/references/regions'),
  searchMaterials: (query, region = null, limit = 10) =>
    api.get('/api/references/materials/search', {
      params: { query, region, limit }
    }),
  searchEquipment: (query, region = null, limit = 10) =>
    api.get('/api/references/equipment/search', {
      params: { query, region, limit }
    }),
}

export const gesnAPI = {
  listBases: () => api.get('/api/gesn/bases'),
  currentBase: () => api.get('/api/gesn/bases/current'),
  upload: (formData) =>
    api.post('/api/gesn/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 10 * 60 * 1000,
    }),
  search: (query, params = {}) =>
    api.get('/api/gesn/search', { params: { query, ...params } }),
  getNorm: (code, baseId = null) =>
    api.get(`/api/gesn/norms/${encodeURIComponent(code)}`, {
      params: baseId ? { base_id: baseId } : {},
    }),
  calculate: (data) => api.post('/api/gesn/calculate', data),
  review: (estimateId, data) =>
    api.post(`/api/gesn/estimates/${estimateId}/review`, data),
  stats: () => api.get('/api/gesn/stats'),
}

export const estimatesAPI = {
  calculate: (data) =>
    api.post('/api/estimates/calculate', data),
  calculateForSheet: (sheetId, region, markupPercent = 15.0) =>
    api.post(`/api/estimates/calculate/${sheetId}`, null, {
      params: { region, markup_percent: markupPercent }
    }),
  getFormatted: (sheetId, region, markupPercent = 15.0) =>
    api.get(`/api/estimates/format/${sheetId}`, {
      params: { region, markup_percent: markupPercent }
    }),
  // Интерактивные сметы
  calculateFromSheet: (data) =>
    api.post('/api/estimates/calculate-from-sheet', data),
  calculateForCard: (data) =>
    api.post('/api/estimates/calculate-for-card', data),
  replaceItem: (estimateId, data) =>
    api.put(`/api/estimates/${estimateId}/replace-item`, data),
  toggleItemExclusion: (estimateId, data) =>
    api.put(`/api/estimates/${estimateId}/toggle-item-exclusion`, data),
  addManualItem: (data) =>
    api.post('/api/estimates/add-manual-item', data),
  searchAlternatives: (estimateId, data) =>
    api.post(`/api/estimates/${estimateId}/search-alternatives`, data),
  fixEstimate: (estimateId) =>
    api.post(`/api/estimates/${estimateId}/fix`),
  unfixEstimate: (estimateId) =>
    api.post(`/api/estimates/${estimateId}/unfix`),
}

export const paymentsAPI = {
  // Список тарифов
  getPlans: () => api.get('/api/payments/plans'),

  // Создать платёж (возвращает confirmation_url ЮKassa)
  createPayment: (planId) =>
    api.post('/api/payments/create', { plan_id: planId }),

  // История моих платежей
  getMyPayments: () => api.get('/api/payments/my'),

  // Моя активная подписка
  getMySubscription: () => api.get('/api/payments/my/subscription'),

  // Проверка статуса платежа (polling после возврата с оплаты)
  checkPaymentStatus: (paymentId) =>
    api.get(`/api/payments/${paymentId}/status`),
}

export const adminAPI = {
  // Список пользователей с подписками и платежами
  getUsers: (params = {}) =>
    api.get('/api/admin/users', { params }),

  // Платежи конкретного пользователя
  getUserPayments: (userId) =>
    api.get(`/api/admin/users/${userId}/payments`),

  // Все платежи
  getPayments: (params = {}) =>
    api.get('/api/admin/payments', { params }),

  // Сводная статистика
  getStats: () => api.get('/api/admin/stats'),
}

export default api
